import React from 'react';
import { Button, FileUpload, OverlaySpinner } from '@insurancemenu/imenu-components-lib';
import { observer } from 'mobx-react-lite';
import { UPLOAD_FILE_TYPES } from '@insurancemenu/imenu-components-lib/dist/components/FileUpload';
import StepHeader from 'components/common/headers/StepHeader';
import { ICensusModule } from 'store/modules/census/iCensusModule';
import ICensusUploadPageStore, { CensusUploadDomainParams } from 'store/modules/census/iCensusUploadPageStore';
import {
  CensusStepSectionWithLoaderStyled,
  CheckSectionWrapperStyled,
  ErrorMessageStyled,
  FileUploadWrapperStyled,
  ValidationErrorMessageStyled,
} from './styles';
import { StepSubtitleStyled } from '../StepSubtitleStyled';
import { CensusStepSectionStyled } from '../CensusStepSectionStyled';
import {
  CENSUS_ERRORS_MSG_TEST_ID,
  CENSUS_UPLOAD_LOADER_TEST_ID,
  CHECK_DATA_BTN_TEST_ID,
  DIFFERENT_FILE_TYPES_MSG_TEST_ID,
  DOWNLOAD_PREVIOUS_FILES_BTN_TEST_ID,
  FILE_UPLOAD_TEST_ID,
} from '../constants';

interface Props {
  pageStore: ICensusUploadPageStore<CensusUploadDomainParams, ICensusModule<CensusUploadDomainParams>>;
}

const CensusUpload: React.FC<Props> = observer(({ pageStore: { censusModule, completeCensusUpload } }) => {
  return (
    <>
      <CensusStepSectionStyled>
        <StepHeader
          stepNumber="1"
          stepTitle="Import Census"
          stepSubtitle={
            <StepSubtitleStyled>
              You can import your completed Census here. Either drag and drop the Census in the dotted box or select the
              button below to open up a dialogue box.
            </StepSubtitleStyled>
          }
        />
        <FileUploadWrapperStyled>
          <FileUpload
            fileMimeTypes={
              censusModule.fileFormat && UPLOAD_FILE_TYPES[censusModule.fileFormat]
                ? [UPLOAD_FILE_TYPES[censusModule.fileFormat]]
                : [UPLOAD_FILE_TYPES.xls, UPLOAD_FILE_TYPES.xlsx, UPLOAD_FILE_TYPES.tsv]
            }
            messageForRejectedFiles="To proceed, please ensure all uploaded files are of the same type (either tsv or xlsx)."
            onChange={censusModule.setFiles}
            data-test-id={FILE_UPLOAD_TEST_ID}
          />
        </FileUploadWrapperStyled>
        {!censusModule.allFilesInOneFormat && (
          <ValidationErrorMessageStyled data-test-id={DIFFERENT_FILE_TYPES_MSG_TEST_ID}>
            To proceed, please ensure all uploaded files are the same file type (tsv or xlsx)
          </ValidationErrorMessageStyled>
        )}
      </CensusStepSectionStyled>
      <CensusStepSectionWithLoaderStyled>
        <StepHeader
          stepNumber="2"
          stepTitle="Check Census"
          stepSubtitle={
            <StepSubtitleStyled>
              We will validate the Census to ensure that all eligible employees can be successfully added for this group
              and that all the required fields are complete.
            </StepSubtitleStyled>
          }
        />
        <CheckSectionWrapperStyled>
          {censusModule.censusHasErrors && (
            <ErrorMessageStyled data-test-id={CENSUS_ERRORS_MSG_TEST_ID}>Something went wrong</ErrorMessageStyled>
          )}
          <Button
            onClick={completeCensusUpload}
            disabled={censusModule.noUploadedFiles || !censusModule.allFilesInOneFormat}
            size="large"
            color="primary"
            data-test-id={CHECK_DATA_BTN_TEST_ID}
          >
            Check Data
          </Button>
        </CheckSectionWrapperStyled>
        {censusModule.isLoading && (
          <OverlaySpinner display={censusModule.isLoading} data-test-id={CENSUS_UPLOAD_LOADER_TEST_ID} />
        )}
      </CensusStepSectionWithLoaderStyled>
      {censusModule.previouslyUploadedFilesUrls && (
        <CensusStepSectionStyled>
          <StepHeader
            stepTitle="Download Latest Census (Sponsor View)"
            stepSubtitle={
              <StepSubtitleStyled>
                Download the most recent census uploaded for this group. This is an admin feature not available to
                non-sponsor users. <b>IMPORTANT:</b> Agency users and employer group users DO NOT have this ability.
              </StepSubtitleStyled>
            }
          />
          <Button
            onClick={censusModule.downloadPreviousFiles}
            size="large"
            type="outlined"
            color="primary"
            data-test-id={DOWNLOAD_PREVIOUS_FILES_BTN_TEST_ID}
          >
            Download
          </Button>
        </CensusStepSectionStyled>
      )}
    </>
  );
});

export default CensusUpload;
